<template>
  <div
    :class="{
      'card': true,
      'is-smallshadow': true,
      'complete': isFormComplete
    }">
    <div
      :class="{
        'card-body': true,
        'width-narrow': !readonly,
        'form-readonly': readonly
      }">
      <h2
        :class="{
          'heading-bigger mb-4': !readonly,
          'heading-small text-grey': readonly
        }">
        {{ $t('inquiryForm.basicInfo') }}
      </h2>

      <div v-if="allDataLoaded">
        <div
          v-if="transactionTypesLoaded"
          class="form-group">
          <label for="transaction-type">{{ $t('inquiryTransactionType.purposeOfCredit') }}</label>

          <template v-if="!readonly">
            <validation-provider
              rules="required"
              name="transaction-type"
              v-slot="{ errors }">

              <f-form-radio
                v-for="(transactionType, index) in allTransactionTypes"
                :key="index"
                :value="transactionType.id"
                v-model="data.transaction_type_id"
                @change="resetTransactionSubject">
                {{ transactionType.name }}
              </f-form-radio>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </template>
          <template v-else>
            {{ getTransactionTypeName(data.transaction_type_id) }}
          </template>
        </div>

        <div class="form-group">
          <label for="property-value">{{ $t('inquiryTransactionType.valueOfProperty') }}</label>

          <template v-if="!readonly">
            <validation-provider
              rules="required|min_value:1"
              name="property-value"
              v-slot="{ errors }">
              <f-input-wrapper append="PLN">
                <f-input
                  min="1"
                  step="1"
                  type="number"
                  v-model="data.property_value"
                  :state="errors.length ? 'invalid' : null"/>
              </f-input-wrapper>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </template>
          <template v-else>
            {{ data.property_value }} PLN
          </template>
        </div>

        <div
          v-if="transactionSubjectsLoaded"
          class="form-group">
          <label for="transaction-subject">{{ $t('inquiryTransactionType.subjectOfSecurity') }}</label>

          <template v-if="!readonly">
            <validation-provider
              name="transaction-subject"
              rules="required"
              v-slot="{ errors }">
              <f-form-radio
                v-for="(transactionSubject, index) in filteredTransactionSubjects"
                :key="index"
                :value="transactionSubject.id"
                @change="resetNeedFinishing"
                v-model="data.transaction_subject_id">
                {{ transactionSubject.name }}
              </f-form-radio>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </template>
          <template v-else>
            {{ getTransactionSubjectName(data.transaction_subject_id) }}
          </template>
        </div>

        <div class="form-group">
          <label for="repayment-period">{{ $t('inquiryOfferLoanSimulation.repaymentPeriod') }}</label>

          <template v-if="!readonly">
            <validation-provider
              rules="required|min_value:1"
              name="repayment-period"
              v-slot="{ errors }">
              <f-input-wrapper :append="$t('ui.years')">
                <f-input
                  min="1"
                  step="1"
                  type="number"
                  v-model="data.repayment_period"
                  :state="errors.length ? 'invalid' : null"/>
              </f-input-wrapper>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </template>
          <template v-else>
            {{ data.repayment_period }} {{ $t('ui.years') }}
          </template>
        </div>

        <div class="form-group">
          <label for="own-contribution">{{ $t('inquiryOffers.ownContribution') }}
            <template v-if="!readonly"> (%)</template>
          </label>

          <template v-if="!readonly">
            <validation-provider
              name="own-contribution"
              rules="required|min_value:0.1|max_value:100"
              v-slot="{ errors }">
              <f-input-wrapper append="%">
                <f-input
                  max="100"
                  min="1"
                  step="1"
                  type="number"
                  v-model="data.own_contribution"
                  :state="errors.length ? 'invalid' : null"/>
              </f-input-wrapper>
              <div class="additional-info">
                <f-icon name="info" />
                <span>{{ $t('inquiryTransactionType.yourOwnContribution') }} {{ data.property_value * (data.own_contribution / 100) }} PLN</span>
              </div>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </template>
          <template v-else>
            {{ data.own_contribution }} %
          </template>
        </div>

        <div class="form-group">
          <label for="expected-loan-installment">{{ $t('inquiryTransactionType.expectedLoanInstallment') }}</label>

          <template v-if="!readonly">
            <validation-provider
              name="expected-loan-installment"
              rules="required|min_value:1"
              v-slot="{ errors }">
              <f-input-wrapper append="PLN">
                <f-input
                  min="1"
                  step="1"
                  type="number"
                  v-model="data.expected_loan_installment"
                  :state="errors.length ? 'invalid' : null"/>
              </f-input-wrapper>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </template>
          <template v-else>
            {{data.expected_loan_installment}} PLN
          </template>
        </div>

        <div class="form-group">
          <label for="measurement">{{ $t('inquiryTransactionType.metric') }}</label>

          <template v-if="!readonly">
            <validation-provider
              rules="required|min_value:1"
              name="measurement"
              v-slot="{ errors }">
              <f-input-wrapper append="m²">
                <f-input
                  min="1"
                  step="1"
                  type="number"
                  v-model="data.measurement"
                  :state="errors.length ? 'invalid' : null"/>
              </f-input-wrapper>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </template>
          <template v-else>
            {{ data.measurement }} m²
          </template>
        </div>

        <div
          v-if="isFinishingVisible"
          class="form-group">
          <label for="need-finishing">{{ $t('inquiryTransactionType.doYouNeedFundsForFinishing') }}</label>

          <template v-if="!readonly">
            <validation-provider
              rules="required"
              name="need-finishing"
              v-slot="{ errors }">
              <f-form-radio
                :value="true"
                v-model="data.is_need_finishing">
                {{ $t('ui.yes') }}
              </f-form-radio>
              <f-form-radio
                :value="false"
                v-model="data.is_need_finishing">
                {{ $t('ui.no') }}
              </f-form-radio>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </template>
          <template v-else>
            <template v-if="data.is_need_finishing">
              {{ $t('ui.yes') }}
            </template>
            <template v-else>
              {{ $t('ui.no') }}
            </template>
          </template>
        </div>

        <div
          v-if="data.is_need_finishing"
          class="form-group">
          <label for="need-finishing-amount">{{ $t('inquiryTransactionType.amountForFinishing') }}</label>

          <template v-if="!readonly">
            <validation-provider
              name="need-finishing-amount"
              rules="required|min_value:1"
              v-slot="{ errors }">
              <f-input-wrapper append="PLN">
                <f-input
                  min="1"
                  step="1"
                  type="number"
                  v-model="data.need_finishing_amount"
                  :state="errors.length ? 'invalid' : null"/>
              </f-input-wrapper>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </template>
          <template v-else>
            {{ data.need_finishing_amount }} PLN
          </template>
        </div>

        <div class="form-group">
          <label for="transaction-additional-information">{{ $t('inquiryHouseholdInfo.additionalInfo') }}</label>

          <template v-if="!readonly">
            <validation-provider
              name="transaction-additional-information"
              v-slot="{ errors }">
              <f-textarea
                v-model="data.transaction_additional_information"
                :rows="6"
                :state="errors.length ? 'invalid' : null"/>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </template>
          <template v-else>
            <template v-if="data.transaction_additional_information && data.transaction_additional_information.length">
              {{ data.transaction_additional_information }}
            </template>
            <template v-else>
              -
            </template>
          </template>
        </div>
      </div>
      <div
        v-if="!allDataLoaded && !loadError"
        class="loading-spinner-wrapper">
        <div class="loading-spinner pb-3 pt-3">
          <span class="loading-spinner-content">
            {{ $t('ui.loadingDataInProgress') }}&hellip;
          </span>
        </div>
      </div>
      <f-alert
        v-if="loadError"
        visible
        theme="warning">
        <span>
          {{ $t('ui.dataLoadError.dataErrorMess') }} <a href="javascript:window.location.reload();" class="lnk lnk-alert">{{ $t('ui.dataLoadError.resetPageButton') }}</a> {{ $t('ui.dataLoadError.forTryAgain') }}
        </span>
      </f-alert>

    </div>

    <div
      v-if="!readonly && allDataLoaded"
      class="card-footer pt-3 pb-4 text-right">
      <f-button
        theme="primary"
        withArrow
        big
        @click.prevent="goToNextTab">
        {{ $t('ui.next') }}
      </f-button>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import FormUtils from '@/utils/FormUtils.js';

export default {
  name: 'inquiry-transaction-type-form',
  props: {
    isEdit: {
      type: Boolean,
      default: false
    },
    index: {
      type: Number,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    },
    isDuplicate: {
      type: Boolean,
      default: false
    },
    propertyValue: {
      type: Number
    },
    repaymentPeriod: {
      type: Number
    },
    ownContribution: {
      type: Number
    },
    expectedLoanInstallment: {
      type: Number
    },
    transactionTypeID: {
      type: Number
    },
    transactionSubjectID: {
      type: Number
    },
    measurementMeters: {
      type: Number
    },
    isNeedFinishing: {
      type: Boolean
    },
    needFinishingAmount: {
      type: Number
    },
    transactionAdditionalInformation: {
      type: String
    },
    isValid: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.transactionTypesLoaded && this.transactionSubjectsLoaded;
    },
    filteredTransactionSubjects () {
      if (this.data.transaction_type_id) {
        return this.allTransactionSubjects.filter(item => item.transaction_types.find(type => type.id === this.data.transaction_type_id));
      }

      return this.allTransactionSubjects;
    },
    isFormComplete() {
      let isComplete = !!this.data.property_value &&
                      !!this.data.repayment_period &&
                      !!this.data.own_contribution &&
                      !!this.data.expected_loan_installment &&
                      !!this.data.transaction_type_id &&
                      !!this.data.transaction_subject_id &&
                      this.data.measurement !== '' &&
                      this.data.measurement !== null &&
                      (
                        !this.data.is_need_finishing ||
                        (
                          !!this.data.is_need_finishing &&
                          !!this.data.need_finishing_amount
                        )
                      );
      isComplete = isComplete && this.isValid;
      if (this.allDataLoaded && (isComplete !== this.previousIsCompleteSet)) {
        Vue.set(this, 'previousIsCompleteSet', isComplete);
        this.$bus.$emit('inquiry-form-part-complete', this.index, isComplete, 'transaction-type');
      }

      return isComplete;
    },
    isFinishingVisible () {
      if (this.data.transaction_type_id && this.data.transaction_subject_id) {
        let selectedTransactionType = this.allTransactionTypes.find(item => item.id === this.data.transaction_type_id);
        let selectedTransactionSubject = this.allTransactionSubjects.find(item => item.id === this.data.transaction_subject_id);

        if (selectedTransactionType && selectedTransactionType.type === 'PRIMARY_MARKET' && selectedTransactionSubject.is_need_finishing) {
          return true;
        }
      }

      return false;
    },
  },
  data() {
    return {
      data: {
        property_value: null,
        repayment_period: null,
        own_contribution: null,
        expected_loan_installment: null,
        transaction_type_id: null,
        transaction_subject_id: null,
        is_need_finishing: null,
        need_finishing_amount: null,
        measurement: null,
        transaction_additional_information: null
      },
      allTransactionTypes: [],
      transactionTypesLoaded: false,
      allTransactionSubjects: [],
      transactionSubjectsLoaded: false,
      loaded: false,
      loadError: false,
      previousIsCompleteSet: false
    };
  },
  mounted () {
    this.loadAdditionalData();

    if (this.isEdit || this.isDuplicate) {
      Vue.nextTick(() => {
        Vue.set(this.data, 'property_value', this.propertyValue);
        Vue.set(this.data, 'repayment_period', this.repaymentPeriod);
        Vue.set(this.data, 'own_contribution', this.ownContribution);
        Vue.set(this.data, 'expected_loan_installment', this.expectedLoanInstallment);
        Vue.set(this.data, 'transaction_type_id', this.transactionTypeID);
        Vue.set(this.data, 'transaction_subject_id', this.transactionSubjectID);
        Vue.set(this.data, 'is_need_finishing', !!this.isNeedFinishing);
        Vue.set(this.data, 'need_finishing_amount', this.needFinishingAmount);
        Vue.set(this.data, 'measurement', this.measurementMeters);
        Vue.set(this.data, 'transaction_additional_information', this.transactionAdditionalInformation);

        this.loaded = true;
      });
    } else {
      this.loaded = true;
    }
  },
  methods: {
    loadAdditionalData() {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/system/transactions-types/items',
        method: 'get',
        outputKey: 'allTransactionTypes',
        loadedKey: 'transactionTypesLoaded',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/system/transactions-subjects/items',
        method: 'get',
        outputKey: 'allTransactionSubjects',
        loadedKey: 'transactionSubjectsLoaded',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });
    },
    resetTransactionSubject () {
      this.data.transaction_subject_id = null;
      this.resetNeedFinishing();
    },
    resetNeedFinishing () {
      this.data.is_need_finishing = null;
      this.data.need_finishing_amount = null;
    },
    goToNextTab () {
      this.$bus.$emit('inquiry-form-go-next-tab', this.index + 1);
    },
    getData () {
      return {
        property_value: this.data.property_value,
        repayment_period: this.data.repayment_period,
        own_contribution: this.data.own_contribution,
        expected_loan_installment: this.data.expected_loan_installment,
        transaction_type_id: this.data.transaction_type_id,
        transaction_subject_id: this.data.transaction_subject_id,
        measurement: this.data.measurement,
        is_need_finishing: this.data.is_need_finishing ? 1 : 0,
        need_finishing_amount: this.data.is_need_finishing ? this.data.need_finishing_amount : 0,
        transaction_additional_information: this.data.transaction_additional_information
      };
    },
    getTransactionTypeName (transactionTypeID) {
      return this.allTransactionTypes.find(item => item.id === transactionTypeID).name;
    },
    getTransactionSubjectName (transactionSubjectID) {
      return this.allTransactionSubjects.find(item => item.id === transactionSubjectID).name;
    }
  }
}
</script>
